import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  CustomButton,
  CustomDropdown,
  CustomTextInput,
  CustomLoader,
} from "../../../components";
import AxiosInstance from "../../../services/Api";
import styles from "./PlansPricing.module.css";

const LIST_PATH = "/dashboard/masters/plans-pricing";
const SLAB_LIST_API = "/saas/list/slab-configuration";
const MODULE_LIST_API = "/saas/module/list";

const PUBLIC_BADGE_OPTIONS = [
  { value: "Recommended", label: "Recommended" },
  { value: "Limited-Time", label: "Limited-Time" },
  { value: "Best Value", label: "Best Value" },
  { value: "Most Popular", label: "Most Popular" },
  { value: "Special Offer", label: "Special Offer" },
  { value: "Others", label: "Others" },
];

const FEATURES_TYPE_OPTIONS = [
  { value: "core", label: "Core Features" },
  { value: "ai_core", label: "AI + Core Features" },
];

const buildModulePayload = (featuresTypeValue) =>
  featuresTypeValue === "ai_core"
    ? { is_core: "", is_ai: 1, is_addon: "", page: "", limit: "" }
    : { is_core: 1, is_ai: "", is_addon: "", page: "", limit: "" };

const RANGE_KEYS = ["range1", "range2", "range3", "range4"];

const emptyPricing = () =>
  RANGE_KEYS.reduce((acc, key) => ({ ...acc, [key]: "" }), {});

const buildInitialState = (plan) => ({
  planName: plan?.name || "",
  planDescription: plan?.description || "",
  publicBadge:
    PUBLIC_BADGE_OPTIONS.find((o) => o.label === plan?.badgeText) ||
    PUBLIC_BADGE_OPTIONS[0],
  customBadgeText: plan?.badgeText || "",
  highlightOnPublicPricing: Boolean(plan?.highlightOnPublicPricing),
  featuresType:
    FEATURES_TYPE_OPTIONS.find((o) => o.value === plan?.featuresType) ||
    FEATURES_TYPE_OPTIONS[0],
  planPricing: plan?.planPricing
    ? { ...emptyPricing(), ...plan.planPricing }
    : emptyPricing(),
  amcPricing: plan?.amcPricing
    ? { ...emptyPricing(), ...plan.amcPricing }
    : emptyPricing(),
});

const buildSlabLabel = (slab) => {
  const parts = [1, 2, 3, 4]
    .map((n) => slab[`range${n}`])
    .filter((v) => v != null);
  return parts.join("/");
};

const buildRangesFromSlab = (slab) =>
  [1, 2, 3, 4]
    .filter((n) => slab[`range${n}`] != null)
    .map((n) => ({ key: `range${n}`, upTo: slab[`range${n}`] }));

// ── Pricing input guards ──────────────────────────────────────────────────────
// Digits 0-9 only, and never a leading zero: "10"/"100"/"2500" are valid,
// "0"/"01"/"05" are not. No negatives, decimals, exponents or symbols.
const NUMBER_ALLOWED_KEYS = new Set([
  "Backspace",
  "Delete",
  "Tab",
  "ArrowLeft",
  "ArrowRight",
  "Home",
  "End",
  "Enter",
]);

// Backstop for pastes, autofill and drops — onKeyDown alone cannot catch those.
const sanitizePricing = (value) =>
  String(value ?? "")
    .replace(/\D/g, "") // digits only
    .replace(/^0+/, "") // drop leading zeros: "0" → "", "05" → "5"
    .slice(0, 8);

const handlePricingKeyDown = (e) => {
  // Let clipboard / select-all / undo shortcuts and caret keys through.
  if (
    (e.ctrlKey || e.metaKey) &&
    ["a", "c", "v", "x", "z"].includes(e.key.toLowerCase())
  )
    return;
  if (NUMBER_ALLOWED_KEYS.has(e.key)) return;

  // Anything that is not a single digit never reaches the field.
  if (!/^\d$/.test(e.key)) {
    e.preventDefault();
    return;
  }

  // A "0" typed at the start of the value would make it a leading zero.
  if (e.key === "0" && (e.target.selectionStart ?? 0) === 0) {
    e.preventDefault();
  }
};

// ── Text guards — block special characters in name/description/badge fields ──
// Letters, digits, spaces, and a few common punctuation marks only.
const TEXT_SANITIZE_FIELDS = new Set([
  "planName",
  "planDescription",
  "customBadgeText",
]);
const sanitizeText = (value) => value.replace(/[^A-Za-z0-9\s.,&'-]/g, "");

const EditPlansPricing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const restoredFormData = location.state?.restoredFormData;
  const restoredRanges = location.state?.restoredRanges;
  const restoredSlabId = location.state?.restoredSlabId;

  // ✅ Always fall back to buildInitialState — prevents crash when navigating
  // fresh to /create (restoredFormData is undefined in that case)
  const [formData, setFormData] = useState(
    () => restoredFormData || buildInitialState(null),
  );
  const [errors, setErrors] = useState({});

  const [slabOptions, setSlabOptions] = useState([]);
  const [selectedSlab, setSelectedSlab] = useState(null);
  const [ranges, setRanges] = useState([]);
  const [slabsLoading, setSlabsLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [moduleList, setModuleList] = useState([]);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [modulesError, setModulesError] = useState("");

  const [navigating, setNavigating] = useState(false);

  // ── fetch slabs ───────────────────────────────────────────────────────────
  const fetchSlabs = useCallback(async () => {
    setSlabsLoading(true);
    setLoadError("");
    try {
      const response = await AxiosInstance.post(SLAB_LIST_API, {
        page: "",
        limit: "",
        status: 1,
      });
      const list = response?.data?.data?.list || [];

      if (!list.length) {
        setLoadError("No slab configurations found. Please create one first.");
        return;
      }

      const options = list.map((slab) => ({
        value: slab.id,
        label: buildSlabLabel(slab),
        slab,
      }));

      setSlabOptions(options);

      const restoredSlabIdLocal = restoredSlabId;
      let defaultOption;

      if (restoredSlabIdLocal) {
        defaultOption = options.find((o) => o.value === restoredSlabIdLocal);
      }

      if (!defaultOption) {
        defaultOption =
          options.find((o) => o.slab.status === "Upcoming") ||
          options.find((o) => o.slab.status === "Active") ||
          options[0];
      }

      setSelectedSlab(defaultOption);

      // ✅ Restore exact ranges from preview navigation — prevents label mismatch on Back
      if (restoredRanges?.length) {
        setRanges(restoredRanges);
      } else {
        setRanges(buildRangesFromSlab(defaultOption.slab));
      }
    } catch (err) {
      setLoadError(
        err?.response?.data?.message ||
          err?.message ||
          "Error loading slab configurations",
      );
    } finally {
      setSlabsLoading(false);
    }
  }, [restoredSlabId, restoredRanges]);

  useEffect(() => {
    fetchSlabs();
  }, [fetchSlabs]);

  const handleSlabChange = (option) => {
    setSelectedSlab(option);
    setRanges(buildRangesFromSlab(option.slab));
    setFormData((prev) => ({
      ...prev,
      planPricing: emptyPricing(),
      amcPricing: emptyPricing(),
    }));
    setErrors((prev) => {
      const cleaned = { ...prev };
      RANGE_KEYS.forEach((k) => {
        delete cleaned[`planPricing_${k}`];
        delete cleaned[`amcPricing_${k}`];
      });
      return cleaned;
    });
  };

  // ── fetch modules ─────────────────────────────────────────────────────────
  const fetchModules = useCallback(async (typeValue) => {
    setModulesLoading(true);
    setModulesError("");
    try {
      const response = await AxiosInstance.post(
        MODULE_LIST_API,
        buildModulePayload(typeValue),
      );
      if (response.data?.status && Array.isArray(response.data.data)) {
        setModuleList(response.data.data);
      } else {
        setModuleList([]);
        throw new Error(response.data?.message || "Failed to fetch modules");
      }
    } catch (err) {
      setModulesError(
        err?.response?.data?.message || err?.message || "Error loading modules",
      );
      setModuleList([]);
    } finally {
      setModulesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchModules(formData.featuresType?.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFeaturesTypeChange = (value) => {
    handleField("featuresType", value);
    fetchModules(value?.value);
  };

  const handleField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    handleField(
      name,
      TEXT_SANITIZE_FIELDS.has(name) ? sanitizeText(value) : value,
    );
  };

  const handlePricingChange = (section, rangeKey, value) => {
    const sanitized = sanitizePricing(value);
    setFormData((prev) => ({
      ...prev,
      [section]: { ...prev[section], [rangeKey]: sanitized },
    }));
    const errorKey = `${section}_${rangeKey}`;
    if (errors[errorKey]) setErrors((prev) => ({ ...prev, [errorKey]: "" }));
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = () => {
    const newErrors = {};

    // ✅ Plan Name — required + min 2 chars
    const trimmedName = formData.planName.trim();
    if (!trimmedName) {
      newErrors.planName = "Plan name is required";
    } else if (trimmedName.length < 2) {
      newErrors.planName = "Plan name must be at least 2 characters";
    }

    if (!formData.planDescription.trim())
      newErrors.planDescription = "Plan description is required";

    // ✅ Custom badge text is mandatory when Public Badge = "Others"
    if (
      formData.publicBadge?.value === "Others" &&
      !formData.customBadgeText.trim()
    )
      newErrors.customBadgeText = "Custom badge text is required";

    if (!selectedSlab) newErrors.slab = "Please select a slab configuration";

    ranges.forEach((range) => {
      if (formData.planPricing[range.key] === "")
        newErrors[`planPricing_${range.key}`] = "Required";
      if (formData.amcPricing[range.key] === "")
        newErrors[`amcPricing_${range.key}`] = "Required";
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── Save & Preview ────────────────────────────────────────────────────────
  const handleSaveAndPreview = () => {
    if (!validate()) return;
    setNavigating(true);
    navigate(`${LIST_PATH}/preview/${id || "new"}`, {
      state: {
        formData,
        ranges,
        slabId: selectedSlab?.value,
        planFeatures: moduleList,
        restoredSlabId: selectedSlab?.value,
        restoredRanges: ranges,
      },
    });
    setNavigating(false);
  };

  if (slabsLoading) {
    return (
      <div>
        <div className={styles.loadingContainer}>
          <CustomLoader text="Loading slab configurations..." />
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* <div className={styles.breadcrumb}>
        <span className={styles.breadcrumbLink} onClick={() => navigate(LIST_PATH)}>
          Plans &amp; Pricing
        </span>
        <span className={styles.breadcrumbSeparator}>&gt;</span>
        <span className={styles.breadcrumbCurrent}>
          {isEditMode ? "Edit Plan" : "Create Plan"}
        </span>
      </div> */}

      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>
            {isEditMode ? "Edit Plan" : "Create Plan"}
          </h1>
        </div>
      </div>

      {loadError && (
        <p style={{ color: "#E23B3B", margin: "12px 0" }}>{loadError}</p>
      )}

      {/* Basic Information */}
      <div className={styles.card}>
        <h2 className={styles.sectionTitle}>Basic Information</h2>
        <div className={styles.formGrid}>
          <div className={styles.fullRow}>
            <CustomTextInput
              label="Plan Name"
              name="planName"
              value={formData.planName}
              onChange={handleInputChange}
              placeholder="e.g. Starter Plan"
              // maxLength={120}
              required
              maxLength={20}
              error={errors.planName}
            />
          </div>

          <div className={styles.fullRow}>
            <CustomTextInput
              label="Plan Description"
              name="planDescription"
              value={formData.planDescription}
              onChange={handleInputChange}
              placeholder="Enter Plan Description"
              multiline
              rows={3}
              maxLength={250}
              required
              error={errors.planDescription}
            />
          </div>

          <CustomDropdown
            label="Public Badge"
            name="publicBadge"
            value={formData.publicBadge}
            onChange={(value) => {
              handleField("publicBadge", value);
              // ✅ Clear customBadgeText and its error when switching away from Others
              if (value?.value !== "Others") {
                handleField("customBadgeText", "");
                setErrors((prev) => ({ ...prev, customBadgeText: "" }));
              }
            }}
            options={PUBLIC_BADGE_OPTIONS}
            isSearchable={false}
            isClearable={false}
          />

          {/* ✅ required prop shown only when Others is selected */}
          <CustomTextInput
            label="Custom Badge Text"
            name="customBadgeText"
            value={formData.customBadgeText}
            onChange={handleInputChange}
            placeholder="Enter a custom badge"
            maxLength={12}
            disabled={formData.publicBadge?.value !== "Others"}
            required={formData.publicBadge?.value === "Others"}
            error={
              formData.publicBadge?.value === "Others"
                ? errors.customBadgeText
                : ""
            }
          />
        </div>
      </div>

      {/* Included Features */}
      <div className={styles.card}>
        <h2 className={styles.sectionTitle}>Included Features</h2>
        <div className={styles.formGrid}>
          <CustomDropdown
            label="Features Type"
            name="featuresType"
            value={formData.featuresType}
            onChange={handleFeaturesTypeChange}
            options={FEATURES_TYPE_OPTIONS}
            isSearchable={false}
            isClearable={false}
          />
        </div>

        {modulesError && (
          <p style={{ color: "#E23B3B", margin: "12px 0" }}>{modulesError}</p>
        )}

        {modulesLoading ? (
          <p style={{ color: "#6B7280", margin: "16px 0" }}>
            Loading modules...
          </p>
        ) : (
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>Plan Features</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {moduleList.map((module) => (
                <tr key={module.id}>
                  <td className={styles.featureNameCell}>{module.name}</td>
                  <td className={styles.featureDescCell}>
                    {module.description}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Slab Details */}
      <div className={styles.card}>
        <h2 className={styles.sectionTitle}>Slab Details</h2>
        <div className={styles.formGrid}>
          <CustomDropdown
            label="Slab"
            name="slab"
            value={selectedSlab}
            onChange={handleSlabChange}
            options={slabOptions}
            isSearchable={false}
            isClearable={false}
            required
            error={errors.slab}
            placeholder="Select slab configuration..."
          />
        </div>
      </div>

      {/* Plan Pricing */}
      {ranges.length > 0 && (
        <div className={styles.card}>
          <h2 className={styles.sectionTitle}>Plan Pricing</h2>
          <div className={styles.formGrid}>
            {ranges.map((range) => (
              <CustomTextInput
                key={range.key}
                label={`Up to ${Number(range.upTo).toLocaleString("en-IN")} users`}
                name={`planPricing_${range.key}`}
                type="text"
                inputMode="numeric"
                value={formData.planPricing[range.key]}
                placeholder="Enter..."
                onChange={(e) =>
                  handlePricingChange("planPricing", range.key, e.target.value)
                }
                onKeyDown={handlePricingKeyDown}
                required
                error={errors[`planPricing_${range.key}`]}
              />
            ))}
          </div>
        </div>
      )}

      {/* AMC Pricing */}
      {ranges.length > 0 && (
        <div className={styles.card}>
          <h2 className={styles.sectionTitle}>AMC Pricing</h2>
          <div className={styles.formGrid}>
            {ranges.map((range) => (
              <CustomTextInput
                key={range.key}
                label={`Up to ${Number(range.upTo).toLocaleString("en-IN")} users`}
                name={`amcPricing_${range.key}`}
                type="text"
                inputMode="numeric"
                value={formData.amcPricing[range.key]}
                placeholder="Enter..."
                onChange={(e) =>
                  handlePricingChange("amcPricing", range.key, e.target.value)
                }
                onKeyDown={handlePricingKeyDown}
                required
                error={errors[`amcPricing_${range.key}`]}
              />
            ))}
          </div>
        </div>
      )}

      <div className={styles.formActions}>
        <CustomButton
          variant="outline-dark"
          fullWidth={false}
          onClick={() => navigate(LIST_PATH)}
          disabled={navigating}
        >
          Cancel
        </CustomButton>
        <CustomButton
          variant="primary"
          fullWidth={false}
          className={styles.btnOrange}
          onClick={handleSaveAndPreview}
          loading={navigating}
          loadingText="Loading preview..."
        >
          Save &amp; Preview
        </CustomButton>
      </div>
    </div>
  );
};

export default EditPlansPricing;
